import { observer } from 'mobx-react';
import { Modal } from '../Modal';
import { useStore } from 'app/context';
import { useState, useEffect, useMemo } from 'react';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';

interface ICsvPreviewModal {
	dataType: string;
	fileName: string;
}

export const CSV_PREVIEW_MODAL_ID = 'csv-preview-modal';

export const CsvPreviewModal = observer((props: ICsvPreviewModal) => {
	const { adminImportsUiStore, uiStore } = useStore();
	const [content, setContent] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');

	const handleDownload = () => {
		adminImportsUiStore.adminImportsStore.downloadJobFile(props.dataType, props.fileName);
	};

	useEffect(() => {
		if (uiStore.visibleModals.includes(CSV_PREVIEW_MODAL_ID) && props.fileName) {
			setLoading(true);
			setError('');
			adminImportsUiStore.adminImportsStore
				.getFileContent(props.dataType, props.fileName)
				.then((data) => {
					setContent(data);
					setLoading(false);
				})
				.catch((err) => {
					setError('Fehler beim Laden der Datei: ' + err.message);
					setLoading(false);
				});
		}
	}, [props.fileName, props.dataType, uiStore.visibleModals]);

	const parsedData = useMemo(() => {
		if (!content) return { headers: [], rows: [] };

		const lines = content.split('\n').filter((line) => line.trim());
		if (lines.length === 0) return { headers: [], rows: [] };

		const delimiter = content.includes(';') ? ';' : ',';
		const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/^"|"$/g, ''));
		const rows = lines.slice(1, 101).map((line) =>
			line.split(delimiter).map((cell) => cell.trim().replace(/^"|"$/g, ''))
		);

		return { headers, rows, totalRows: lines.length - 1 };
	}, [content]);

	return (
		<Modal modalId={CSV_PREVIEW_MODAL_ID} title={`CSV Vorschau: ${props.fileName}`} size="fullscreen">
			<div className="csv-preview">
				{loading && <div className="notification is-info">Lade Datei...</div>}
				{error && <div className="notification is-danger">{error}</div>}
				{!loading && !error && content && (
					<>
						<div className="csv-preview__info">
							<span>
								Zeilen: {parsedData.totalRows} (zeige max. 100) | Spalten: {parsedData.headers.length}
							</span>
							<Button type="button" className="button is-primary is-small" onClick={handleDownload}>
								<Icon iconClass="download" /> Herunterladen
							</Button>
						</div>
						<div className="csv-preview__table-container">
							<table className="table is-bordered is-striped is-narrow is-fullwidth">
								<thead>
									<tr>
										<th>#</th>
										{parsedData.headers.map((header, idx) => (
											<th key={idx}>{header}</th>
										))}
									</tr>
								</thead>
								<tbody>
									{parsedData.rows.map((row, rowIdx) => (
										<tr key={rowIdx}>
											<td>{rowIdx + 1}</td>
											{row.map((cell, cellIdx) => (
												<td key={cellIdx}>{cell}</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
});
